import { ChecklistType } from "@/utilities/checkListsSchema";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, IconButton, Typography } from "@mui/material";
import { useLocale } from "../../providers/LocaleProvider";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import { Paper } from "../Paper";

interface SectionItemProps {
  section?: ChecklistType["checklist"]["sections"][number];
  sectionIndex: number;
  selectedSectionIndex: number;
  onSelect: (sectionIndex: number) => void;
  onRemove: (sectionIndex: number) => void;
  canRemove: boolean;
  hasError?: boolean;
  scrollToTop: () => void;
}

export default function SectionItem({
  section,
  sectionIndex,
  selectedSectionIndex,
  onSelect,
  onRemove,
  canRemove,
  hasError = false,
  scrollToTop
}: SectionItemProps) {
  const { translate } = useLocale();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section?.id || ""
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Paper
        sx={{
          display: "flex",
          alignItems: "flex-start",
          marginBottom: 2,
          padding: 0,
          borderRadius: "8px",
          width: "100%",
          cursor: "pointer",
          backgroundColor: selectedSectionIndex === sectionIndex && "rgb(248, 248, 254) !important",
          "&:hover": {
            backgroundColor: "rgba(238, 238, 238, 0.36) !important",
            scale: "1.01"
          },
          border: hasError && "1px solid var(--torch-red-tr-500)"
        }}
      >
        <IconButton
          className='drag-handle'
          {...attributes}
          {...listeners}
          sx={{
            transition: "opacity 0.2s ease-in-out",
            "&:hover": { backgroundColor: "#DBEAFE" },
            borderRadius: "5px",
            marginLeft: 1,
            cursor: "grab",
            "&:active": { cursor: "grabbing" }
          }}
        >
          <DragIndicatorIcon fontSize='small' />
        </IconButton>

        <Typography
          className='klayo-paper_title'
          onClick={() => {
            onSelect(sectionIndex);
            scrollToTop();
          }}
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            flex: 1,
            cursor: "pointer",
            padding: "6px 10px 6px 6px",
            width: "100%"
          }}
        >
          {section?.title ||
            translate("settingsViews.checklists.sectionPlaceholder", {
              number: (section?.displayOrder || 0) + 1
            })}
        </Typography>
        {canRemove && (
          <IconButton
            className='remove-icon'
            onClick={() => onRemove(sectionIndex)}
            sx={{
              transition: "opacity 0.2s ease-in-out",
              "&:hover": { backgroundColor: "#DBEAFE" },
              borderRadius: "5px",
              marginRight: 1
            }}
          >
            <RemoveIcon />
          </IconButton>
        )}
      </Paper>
    </div>
  );
}

export const CustomSectionPreview = ({
  section
}: {
  section: ChecklistType["checklist"]["sections"][number];
}) => {
  const { translate } = useLocale();
  return (
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: 2,
        padding: 1,
        borderRadius: "8px",
        width: "95%",
        cursor: "pointer",
        "&:hover .remove-icon": {
          opacity: 1
        },
        border: "1px solid #3b82f6",
        rotate: "2deg"
      }}
    >
      <Box display='flex' alignItems='center' gap={1}>
        <Typography className='klayo-paper_title' variant='body1' fontWeight={500} noWrap>
          {section?.title ||
            translate("settingsViews.checklists.sectionPlaceholder", {
              number: section?.displayOrder + 1
            })}
        </Typography>
      </Box>
    </Paper>
  );
};
